import { Nav } from '@accedo/spatial-nav';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Link } from 'preact-router';
import clsx from 'clsx';
import { KeyCodes, VirtualKeys } from '@accedo/virtual-key';
import { NavScroll, VERTICAL } from '@accedo/virtual-scroll';
import KeyEventHelper from '##/utils/KeyEventHelper';
import { nav } from '##/init';

const styles = stylesheet`
  @import '##/styles/import.scss';
  .nav-container {
    background: $dark-gray-2;
    width: pxToVw(211);
    height: 100%;
    margin-left: pxToVw(84);
    padding-top: pxToVh(55);
    font-family: barlow;
    box-shadow: 4px 0 pxToVh(20) 0 $pure-black;
  }

  .secondary-nav-scroll {
    overflow: hidden;
  }

  .hidden {
    visibility: hidden;
    width: 0;
  }

  .nav-title {
    font-size: pxToVh(14);
    margin-bottom: pxToVh(10);
    padding: pxToVh(15) pxToVw(25);
    opacity: 0.5;
  }

  .nav-item {
    padding: pxToVh(12) pxToVw(25);
    padding-right: 0;
    margin: pxToVh(3) 0;
    width: pxToVw(211);
    font-size: pxToVh(18);
    color: rgb(200, 200, 200);
    &.active-item {
      background: rgb(80, 80, 80);
      color: $off-white;
    }
  }

  :global(.current-focus) .nav-item {
      background: transparent linear-gradient(90deg, $ten-play-blue 0%, $light-blue 100%) 0% 0% no-repeat padding-box;
      color: $off-white;
  }
  
`;

export interface SecondaryNavItem {
  text: string;
  key: string;
}

export interface SecondaryNavProps {
  title: string;
  items: SecondaryNavItem[];
  setShowing?: (showing: boolean) => void;
  onSelect: (key: string, secondaryNavItemId: string) => void;
  hidden: boolean;
  navId: string;
  activeKey: string;
  className?: string;
  navTitleClassName?: string;
}

export const SecondaryNav: FunctionalComponent<SecondaryNavProps> = ({
  title,
  navTitleClassName,
  items,
  setShowing,
  hidden,
  navId,
  onSelect,
  activeKey,
  className,
  children,
}) => {
  const onActiveChange = (active: boolean): void => {
    if (setShowing) {
      setShowing(active);
    }
  };

  const onKeydown = (keyName: VirtualKeys): boolean => {
    const firstItemNavId = `${navId}-item-0`;
    const lastItemNavId = `${navId}-item-${items.length - 1}`;
    if (
      keyName === KeyCodes.vKey.UP &&
      nav.currentFocusNodeId === firstItemNavId
    ) {
      nav.assignFocus(lastItemNavId);
      return true;
    }
    if (
      keyName === KeyCodes.vKey.DOWN &&
      nav.currentFocusNodeId === lastItemNavId
    ) {
      nav.assignFocus(firstItemNavId);
      return true;
    }
    return false;
  };

  useEffect(() => {
    KeyEventHelper.addKeydownListener(onKeydown, 'secondary-nav');
    return () => {
      KeyEventHelper.removeKeydownListener('secondary-nav');
    };
  }, [items]);

  return (
    <div
      className={clsx(styles.navContainer, hidden && styles.hidden, className)}
    >
      <div className={clsx(styles.navTitle, navTitleClassName)}>{title}</div>
      {children}
      <NavScroll
        orientation={VERTICAL}
        id={navId}
        scrollEndBoundary={12}
        outerClassName={styles.secondaryNavScroll}
        navProps={{
          onFocusWithinChange: (isFocusWithin: boolean) => {
            onActiveChange(isFocusWithin);
          },
        }}
      >
        {items.map((item, idx) => {
          const itemId = `${navId}-item-${idx}`;
          return (
            <div id={itemId} key={itemId}>
              <Nav.Focusable
                id={itemId}
                onSelect={() => onSelect(item.key, itemId)}
              >
                <Link>
                  <div
                    className={clsx(
                      styles.navItem,
                      activeKey === item.key && styles.activeItem,
                    )}
                  >
                    {item.text}
                  </div>
                </Link>
              </Nav.Focusable>
            </div>
          );
        })}
      </NavScroll>
    </div>
  );
};
